"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, type RefObject } from "react";

/**
 * TOASTS ABOVE AN OPEN DIALOG.
 *
 * A v2 `Dialog` is a native `<dialog>` opened with `showModal()`, which puts it
 * in the browser's top layer — above every z-index on the page, with a
 * backdrop that blurs everything beneath. The toaster lived beneath it, so any
 * failure raised while a dialog was open (a deposit under the minimum, a
 * withdrawal short of the fee, an offer below the listing) toasted the
 * server's reason where nobody could see it.
 *
 * The toaster's root is made a manual popover, which also lives in the top
 * layer; the top layer is ordered by insertion, so each time a dialog opens it
 * announces it and the toaster steps back on top (hide, then show again).
 * Without the Popover API (older browsers) nothing changes.
 */
const OPENED = "v2:dialog-opened";

/** Called by `Dialog` right after `showModal()`. */
export function announceDialogOpened() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OPENED));
}

/** Keep this element in the top layer, above any dialog opened after it. */
export function useAboveDialogs(ref) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== ref) {
    t0 = () => {
      const el = ref.current;
      if (!el || typeof el.showPopover !== "function") {
        return;
      }
      el.popover = "manual";
      const raise = () => {
        try {
          if (el.matches(":popover-open")) {
            el.hidePopover();
          }
          el.showPopover();
        } catch {}
      };
      raise();
      window.addEventListener(OPENED, raise);
      return () => {
        window.removeEventListener(OPENED, raise);
        try {
          el.hidePopover();
        } catch {}
      };
    };
    t1 = [ref];
    $[0] = ref;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}